import React from 'react'
import { apiDelete, apiGet, apiJson, apiPatch, apiPost, apiPostMultipart, isApiError } from './api'
import type {
  ApiError,
  AuditRow,
  BackupsList,
  Capabilities,
  InventoryEditRequest,
  InventoryEditResponse,
  ItemRegistry,
  InstalledMap,
  InstalledPlugin,
  LifecycleAction,
  LogLine,
  PeopleState,
  PendingPlugin,
  PlayerRecord,
  PlayerSnapshot,
  PropertiesState,
  QuickActionRequest,
  QuickActionResponse,
  Server,
  ServerId,
  Session,
  SettingsState,
  ShulkerContents,
  ToastPush,
  User,
  VpsStats,
} from './types'

const SERVERS_POLL_MS = 5_000
const SYSTEM_POLL_MS = 5_000
const LOGS_POLL_MS = 2_500
const AUDIT_POLL_MS = 10_000
const PLAYERS_POLL_MS = 15_000
const PLUGINS_POLL_MS = 10_000
const LOCAL_CONSOLE_ECHO_LIMIT = 200

interface ResourceState<T> {
  data: T | null
  error: ApiError | null
  loading: boolean
  refetch: () => void
  mutate: (updater: (prev: T | null) => T | null) => void
}

interface PluginsPayload {
  pending: PendingPlugin[]
  installed: InstalledMap
}

interface LocalConsoleEcho {
  atMs: number
  line: LogLine
}

const DEFAULT_VPS: VpsStats = {
  cpu: 0,
  ram: 0,
  ramUsedGb: 0,
  ramTotalGb: 0,
  disk: 0,
  diskUsedGb: 0,
  diskTotalGb: 0,
  load: [0, 0, 0],
  cpuSpark: [],
  ramSpark: [],
  diskSpark: [],
}

function useResource<T>(
  path: string | null,
  pollMs: number,
  initial: T | null = null,
  transform?: (raw: unknown) => T,
): ResourceState<T> {
  const [state, setState] = React.useState<{ data: T | null; error: ApiError | null; loading: boolean }>(
    () => ({ data: initial, error: null, loading: path !== null }),
  )
  const pathRef = React.useRef(path)
  const [tick, setTick] = React.useState(0)

  React.useEffect(() => {
    pathRef.current = path
  }, [path])

  React.useEffect(() => {
    if (!path) {
      setState({ data: initial, error: null, loading: false })
      return
    }
    let cancelled = false
    const controller = new AbortController()

    async function load(showLoading: boolean) {
      if (!path) return
      if (showLoading) setState(prev => ({ ...prev, loading: true }))
      try {
        const raw = await apiGet<unknown>(path, { signal: controller.signal })
        if (cancelled) return
        const data = transform ? transform(raw) : (raw as T)
        setState({ data, error: null, loading: false })
      } catch (error) {
        if (cancelled) return
        if ((error as { name?: string }).name === 'AbortError') return
        if (isApiError(error)) {
          setState({ data: null, error, loading: false })
        } else {
          setState({ data: null, error: { status: 0, message: 'Network error' }, loading: false })
        }
      }
    }

    void load(true)
    const id = pollMs > 0 ? window.setInterval(() => { void load(false) }, pollMs) : 0

    return () => {
      cancelled = true
      controller.abort()
      if (id) window.clearInterval(id)
    }
  }, [path, pollMs, tick])

  const refetch = React.useCallback(() => setTick(n => n + 1), [])
  const mutate = React.useCallback((updater: (prev: T | null) => T | null) => {
    setState(prev => ({ ...prev, data: updater(prev.data) }))
  }, [])
  return { ...state, refetch, mutate }
}

export function usePanelLogic(push: ToastPush) {
  const [session, setSession] = React.useState<Session>({ status: 'anonymous', userId: null, role: null })
  const [viewer, setViewer] = React.useState<User | null>(null)
  const [capabilities, setCapabilities] = React.useState<Capabilities>({
    canLifecycle: false,
    canManagePlugins: false,
    canUseRawRcon: false,
    canViewAllAudit: false,
    canEditSettings: false,
    canViewFullConsole: false,
    canManageUsers: false,
    canManagePeople: false,
    canEditProperties: false,
    canRawEditProperties: false,
    canManageBackups: false,
    canRestoreBackup: false,
    canDownloadWorld: false,
    canDownloadLogs: false,
    canEditInventory: false,
  })
  const [meLoading, setMeLoading] = React.useState(true)
  const [devMode, setDevMode] = React.useState(false)
  const [serverLogs, setServerLogs] = React.useState<Record<ServerId, LogLine[]>>({})
  const [localConsoleEchoes, setLocalConsoleEchoes] = React.useState<Record<ServerId, LocalConsoleEcho[]>>({})

  const refreshMe = React.useCallback(async () => {
    try {
      const me = await apiGet<{ user: User; capabilities: Capabilities; devMode?: boolean }>('/me')
      setViewer(me.user)
      setCapabilities(me.capabilities)
      setDevMode(Boolean(me.devMode))
      setSession({ status: 'authenticated', userId: me.user.id, role: me.user.role })
    } catch (error) {
      if (isApiError(error) && error.status === 401) {
        setViewer(null)
        setSession({ status: 'anonymous', userId: null, role: null })
      } else if (isApiError(error) && error.status === 403) {
        setViewer(null)
        setSession({ status: 'unauthorized', userId: null, role: null })
      }
    } finally {
      setMeLoading(false)
    }
  }, [])

  React.useEffect(() => {
    void refreshMe()
    const onFocus = () => { void refreshMe() }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [refreshMe])

  const isAuthed = session.status === 'authenticated'

  const serversRes = useResource<Server[]>(isAuthed ? '/servers' : null, SERVERS_POLL_MS, [])
  const vpsRes = useResource<VpsStats>(isAuthed ? '/system' : null, SYSTEM_POLL_MS, DEFAULT_VPS)
  const auditRes = useResource<AuditRow[]>(isAuthed ? '/audit?limit=100' : null, AUDIT_POLL_MS, [])
  const playersRes = useResource<PlayerRecord[]>(isAuthed ? '/players' : null, PLAYERS_POLL_MS, [])
  const pluginsRes = useResource<PluginsPayload>(
    isAuthed ? '/plugins' : null,
    PLUGINS_POLL_MS,
    { pending: [], installed: {} },
  )
  const usersRes = useResource<User[]>(
    isAuthed && capabilities.canManageUsers ? '/users' : null,
    30_000,
    [],
  )
  const backupsRes = useResource<BackupsList>(
    isAuthed && capabilities.canManageBackups ? '/backups' : null,
    30_000,
    { archives: [], freeDiskGb: 0 },
  )

  const servers = serversRes.data ?? []
  const audit = auditRes.data ?? []
  const players = playersRes.data ?? []
  const plugins = pluginsRes.data ?? { pending: [], installed: {} }
  const vps = vpsRes.data ?? DEFAULT_VPS
  const users = usersRes.data ?? (viewer ? [viewer] : [])
  const settings: SettingsState = React.useMemo(() => ({ users }), [users])
  const logs = React.useMemo(() => {
    const result: Record<ServerId, LogLine[]> = {}
    const serverIds = new Set<ServerId>([
      ...Object.keys(serverLogs),
      ...Object.keys(localConsoleEchoes),
    ])

    serverIds.forEach(serverId => {
      result[serverId] = mergeConsoleLogs(serverLogs[serverId] ?? [], localConsoleEchoes[serverId] ?? [])
    })

    return result
  }, [serverLogs, localConsoleEchoes])

  // Per-server logs polling
  React.useEffect(() => {
    if (!isAuthed) {
      setServerLogs({})
      setLocalConsoleEchoes({})
      return
    }
    const controllers: AbortController[] = []
    const ids: number[] = []
    servers.forEach(server => {
      const controller = new AbortController()
      controllers.push(controller)
      const pull = async () => {
        try {
          const lines = await apiGet<LogLine[]>(`/servers/${server.id}/logs?lines=200`, { signal: controller.signal })
          setServerLogs(prev => ({ ...prev, [server.id]: lines }))
        } catch (error) {
          if ((error as { name?: string }).name === 'AbortError') return
        }
      }
      void pull()
      ids.push(window.setInterval(() => { void pull() }, LOGS_POLL_MS))
    })
    return () => {
      controllers.forEach(c => c.abort())
      ids.forEach(id => window.clearInterval(id))
    }
  }, [isAuthed, servers.map(s => s.id).join(',')])

  const login = React.useCallback(async (username: string, password: string) => {
    try {
      const res = await apiPost<{ user: User; capabilities: Capabilities; devMode?: boolean }>('/auth/login', { username, password })
      setViewer(res.user)
      setCapabilities(res.capabilities)
      setDevMode(Boolean(res.devMode))
      setSession({ status: 'authenticated', userId: res.user.id, role: res.user.role })
      push({ variant: 'success', title: 'Signed in', sub: res.user.displayName })
    } catch (error) {
      if (isApiError(error)) {
        if (error.status === 429) {
          push({ variant: 'warn', title: 'Too many attempts', sub: 'Try again in a minute.' })
        } else if (error.status === 401) {
          push({ variant: 'error', title: 'Invalid username or password' })
        } else {
          push({ variant: 'error', title: 'Sign in failed', sub: error.message })
        }
      }
      throw error
    }
  }, [push])

  const logout = React.useCallback(async () => {
    try {
      await apiPost('/auth/logout')
    } catch {
      // ignore network failure — we log out locally regardless.
    }
    setViewer(null)
    setSession({ status: 'anonymous', userId: null, role: null })
    push({ variant: 'info', title: 'Signed out' })
  }, [push])

  const runLifecycleAction = React.useCallback(async (
    serverId: ServerId,
    action: LifecycleAction,
    announce: boolean,
  ) => {
    if (!capabilities.canLifecycle) {
      push({ variant: 'warn', title: '403 — Lifecycle actions are admin only.' })
      return
    }
    try {
      await apiPost<Server>(`/servers/${serverId}/lifecycle`, { action, announce }, newKey())
      push({
        variant: action === 'restart' ? 'info' : 'success',
        title: `${capitalize(action)} queued`,
        sub: `systemctl ${action} ${serverId}`,
        mono: true,
      })
      serversRes.refetch()
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, `Could not ${action} ${serverId}`)
      serversRes.refetch()
    }
  }, [capabilities.canLifecycle, push, serversRes, auditRes])

  const runQuickAction = React.useCallback(async (serverId: ServerId, request: QuickActionRequest) => {
    try {
      const res = await apiPost<QuickActionResponse>(
        `/servers/${serverId}/rcon`,
        { template: request.template, payload: request.payload ?? {} },
        newKey(),
      )
      const feedback = describeQuickAction(request, res, viewer)
      push({ variant: 'success', title: feedback.title, sub: feedback.sub, mono: feedback.mono })
      const consoleLine = feedback.consoleLine
      if (consoleLine) {
        setLocalConsoleEchoes(prev => appendConsoleEcho(prev, serverId, consoleLine))
      }
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Command rejected')
    }
  }, [push, auditRes, viewer])

  const sendConsoleCommand = React.useCallback((serverId: ServerId, input: string) => {
    void runQuickAction(serverId, { template: 'raw_rcon', payload: { cmd: input } })
  }, [runQuickAction])

  const clearConsole = React.useCallback(async (serverId: ServerId) => {
    setServerLogs(prev => ({ ...prev, [serverId]: [] }))
    setLocalConsoleEchoes(prev => ({ ...prev, [serverId]: [] }))
    try {
      await apiPost<void>(`/servers/${serverId}/logs/clear`, undefined, newKey())
      push({ variant: 'info', title: 'Console cleared', sub: serverId, mono: true })
    } catch (error) {
      handleMutationError(error, push, 'Could not clear console')
      try {
        const lines = await apiGet<LogLine[]>(`/servers/${serverId}/logs?lines=200`)
        setServerLogs(prev => ({ ...prev, [serverId]: lines }))
      } catch {
        // ignore secondary fetch failure — next poll will refresh
      }
    }
  }, [push])

  const approvePlugin = React.useCallback(async (plugin: PendingPlugin, target: ServerId, restartAfter: boolean) => {
    if (!capabilities.canManagePlugins) {
      push({ variant: 'warn', title: '403 — Plugin management is admin only.' })
      return
    }
    try {
      await apiPost(`/plugins/${plugin.id}/approve`, { serverId: target, restartAfter }, newKey())
      push({
        variant: 'success',
        title: 'Plugin approved',
        sub: `${plugin.yml.name} → ${target}`,
      })
      pluginsRes.refetch()
      serversRes.refetch()
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Approve failed')
    }
  }, [capabilities.canManagePlugins, push, pluginsRes, serversRes, auditRes])

  const rejectPlugin = React.useCallback(async (plugin: PendingPlugin, reason: string) => {
    if (!capabilities.canManagePlugins) {
      push({ variant: 'warn', title: '403 — Plugin management is admin only.' })
      return
    }
    try {
      await apiPost(`/plugins/${plugin.id}/reject`, { reason }, newKey())
      push({ variant: 'info', title: 'Plugin rejected', sub: plugin.filename })
      pluginsRes.refetch()
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Reject failed')
    }
  }, [capabilities.canManagePlugins, push, pluginsRes, auditRes])

  const toggleInstalledPlugin = React.useCallback(async (serverId: ServerId, plugin: InstalledPlugin) => {
    if (!capabilities.canManagePlugins) {
      push({ variant: 'warn', title: '403 — Plugin management is admin only.' })
      return
    }
    const filename = plugin.filename
    if (!filename) {
      push({ variant: 'error', title: 'Cannot toggle — unknown filename' })
      return
    }
    try {
      const updated = await apiPatch<InstalledPlugin>(
        `/servers/${serverId}/plugins/${encodeURIComponent(filename)}`,
        { enabled: !plugin.enabled },
      )
      pluginsRes.mutate(prev => {
        if (!prev) return prev
        const list = prev.installed[serverId] ?? []
        const next = list.map(p => (p.filename === filename ? updated : p))
        return { ...prev, installed: { ...prev.installed, [serverId]: next } }
      })
      push({
        variant: 'success',
        title: plugin.enabled ? 'Plugin disabled' : 'Plugin enabled',
        sub: `${plugin.name} · takes effect after restart`,
      })
      serversRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Toggle failed')
    }
  }, [capabilities.canManagePlugins, push, pluginsRes, serversRes])

  const deleteInstalledPlugin = React.useCallback(async (serverId: ServerId, plugin: InstalledPlugin) => {
    if (!capabilities.canManagePlugins) {
      push({ variant: 'warn', title: '403 — Plugin management is admin only.' })
      return
    }
    const filename = plugin.filename
    if (!filename) {
      push({ variant: 'error', title: 'Cannot delete — unknown filename' })
      return
    }
    try {
      await apiDelete(`/servers/${serverId}/plugins/${encodeURIComponent(filename)}`)
      pluginsRes.mutate(prev => {
        if (!prev) return prev
        const list = prev.installed[serverId] ?? []
        const next = list.filter(p => p.filename !== filename)
        return { ...prev, installed: { ...prev.installed, [serverId]: next } }
      })
      push({ variant: 'info', title: 'Plugin moved to trash', sub: plugin.name })
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Delete failed')
    }
  }, [capabilities.canManagePlugins, push, pluginsRes, auditRes])

  const uploadPlugin = React.useCallback(async (file: File) => {
    if (!capabilities.canManagePlugins) {
      push({ variant: 'warn', title: '403 — Plugin upload is admin only.' })
      return
    }
    const form = new FormData()
    form.append('file', file)
    try {
      const res = await apiPostMultipart<PendingPlugin>('/plugins/upload', form, newKey())
      push({
        variant: res.flagged ? 'warn' : 'success',
        title: res.flagged ? 'Uploaded (flagged)' : 'Uploaded',
        sub: `${res.yml.name ?? res.filename} — awaiting approval`,
      })
      pluginsRes.refetch()
      auditRes.refetch()
    } catch (error) {
      handleMutationError(error, push, 'Upload failed')
    }
  }, [capabilities.canManagePlugins, push, pluginsRes, auditRes])

  const fetchPeople = React.useCallback(async (serverId: ServerId): Promise<PeopleState> => {
    return apiGet<PeopleState>(`/servers/${serverId}/people`)
  }, [])

  const mutatePeople = React.useCallback(async (
    serverId: ServerId,
    kind: 'whitelist' | 'ops' | 'bans',
    action: 'add' | 'remove',
    body: { name: string; reason?: string | null },
    verb: string,
  ): Promise<boolean> => {
    if (!capabilities.canManagePeople) {
      push({ variant: 'warn', title: '403 — People management is admin only.' })
      return false
    }
    const path = action === 'add'
      ? `/servers/${serverId}/people/${kind}`
      : `/servers/${serverId}/people/${kind}/remove`
    try {
      await apiPost(path, body, newKey())
      push({ variant: 'success', title: `${verb} ${body.name}`, sub: kind, mono: true })
      auditRes.refetch()
      return true
    } catch (error) {
      handleMutationError(error, push, `Could not ${verb.toLowerCase()} ${body.name}`)
      return false
    }
  }, [capabilities.canManagePeople, push, auditRes])

  const fetchProperties = React.useCallback(async (serverId: ServerId): Promise<PropertiesState> => {
    return apiGet<PropertiesState>(`/servers/${serverId}/properties`)
  }, [])

  const fetchLivePlayers = React.useCallback(async (
    serverId: ServerId,
    signal?: AbortSignal,
  ): Promise<PlayerSnapshot[]> => {
    return apiGet<PlayerSnapshot[]>(`/servers/${serverId}/players/live`, { signal })
  }, [])

  const fetchOfflinePlayers = React.useCallback(async (
    serverId: ServerId,
    signal?: AbortSignal,
  ): Promise<PlayerSnapshot[]> => {
    return apiGet<PlayerSnapshot[]>(`/servers/${serverId}/players/offline`, { signal })
  }, [])

  const fetchItemRegistry = React.useCallback(async (
    serverId: ServerId,
    signal?: AbortSignal,
  ): Promise<ItemRegistry> => {
    return apiGet<ItemRegistry>(`/servers/${serverId}/items`, { signal })
  }, [])

  const editPlayerInventory = React.useCallback(async (
    serverId: ServerId,
    playerName: string,
    request: InventoryEditRequest,
  ): Promise<InventoryEditResponse | null> => {
    if (!capabilities.canEditInventory) {
      push({ variant: 'warn', title: '403 - Inventory editing is owner only.' })
      return null
    }
    try {
      const res = await apiPost<InventoryEditResponse>(
        `/servers/${serverId}/players/live/${encodeURIComponent(playerName)}/inventory`,
        request,
        newKey(),
      )
      push({ variant: 'success', title: 'Inventory saved', sub: `${playerName} · ${res.appliedSlots.length} slot(s)` })
      auditRes.refetch()
      return res
    } catch (error) {
      handleMutationError(error, push, 'Inventory save failed')
      return null
    }
  }, [capabilities.canEditInventory, push, auditRes])

  const fetchShulkerContents = React.useCallback(async (
    serverId: ServerId,
    playerName: string,
    slot: number,
    signal?: AbortSignal,
  ): Promise<ShulkerContents> => {
    return apiGet<ShulkerContents>(
      `/servers/${serverId}/players/live/${encodeURIComponent(playerName)}/inventory/${slot}/container`,
      { signal },
    )
  }, [])

  const editShulkerContents = React.useCallback(async (
    serverId: ServerId,
    playerName: string,
    slot: number,
    request: InventoryEditRequest,
  ): Promise<InventoryEditResponse | null> => {
    if (!capabilities.canEditInventory) {
      push({ variant: 'warn', title: '403 - Inventory editing is owner only.' })
      return null
    }
    try {
      const res = await apiPost<InventoryEditResponse>(
        `/servers/${serverId}/players/live/${encodeURIComponent(playerName)}/inventory/${slot}/container`,
        request,
        newKey(),
      )
      push({ variant: 'success', title: 'Shulker saved', sub: `${playerName} · ${res.appliedSlots.length} slot(s)` })
      auditRes.refetch()
      return res
    } catch (error) {
      handleMutationError(error, push, 'Shulker save failed')
      return null
    }
  }, [capabilities.canEditInventory, push, auditRes])

  const writeProperties = React.useCallback(async (
    serverId: ServerId,
    body: { edits?: Record<string, string | number | boolean>; raw?: string | null },
  ): Promise<PropertiesState | null> => {
    if (!capabilities.canEditProperties) {
      push({ variant: 'warn', title: '403 — Properties are admin only.' })
      return null
    }
    if (body.raw != null && !capabilities.canRawEditProperties) {
      push({ variant: 'warn', title: '403 — Raw-mode edit is owner only.' })
      return null
    }
    try {
      const res = await apiJson<PropertiesState>('PUT', `/servers/${serverId}/properties`, body, { idempotencyKey: newKey() })
      push({ variant: 'success', title: 'server.properties saved', sub: body.raw != null ? 'raw edit' : 'applied' })
      auditRes.refetch()
      serversRes.refetch()
      return res
    } catch (error) {
      handleMutationError(error, push, 'Save failed')
      return null
    }
  }, [capabilities.canEditProperties, capabilities.canRawEditProperties, push, auditRes, serversRes])

  const triggerBackup = React.useCallback(async (serverId: ServerId, note: string | null): Promise<boolean> => {
    if (!capabilities.canManageBackups) {
      push({ variant: 'warn', title: '403 — Backups are admin only.' })
      return false
    }
    try {
      await apiPost(`/servers/${serverId}/backups`, { note: note ?? undefined }, newKey())
      push({ variant: 'success', title: 'Backup started', sub: serverId })
      backupsRes.refetch()
      serversRes.refetch()
      auditRes.refetch()
      return true
    } catch (error) {
      handleMutationError(error, push, 'Backup failed')
      return false
    }
  }, [capabilities.canManageBackups, push, backupsRes, serversRes, auditRes])

  const restoreBackup = React.useCallback(async (
    serverId: ServerId,
    archive: string,
    confirm: string,
  ): Promise<boolean> => {
    if (!capabilities.canRestoreBackup) {
      push({ variant: 'warn', title: '403 — Restore is owner only.' })
      return false
    }
    try {
      await apiPost(`/servers/${serverId}/restore`, { archive, confirm }, newKey())
      push({ variant: 'success', title: 'Restore complete', sub: `${archive} — start server manually` })
      backupsRes.refetch()
      serversRes.refetch()
      auditRes.refetch()
      return true
    } catch (error) {
      handleMutationError(error, push, 'Restore failed')
      return false
    }
  }, [capabilities.canRestoreBackup, push, backupsRes, serversRes, auditRes])

  const saveMapping = React.useCallback(async (userId: string, mcName: string) => {
    if (!capabilities.canEditSettings) {
      push({ variant: 'warn', title: '403 — Owner only.' })
      return
    }
    try {
      await apiPost(`/settings/mapping`, { userId, mcUsername: mcName })
      push({ variant: 'success', title: 'Mapping saved', sub: `${userId} → ${mcName}` })
      usersRes.refetch()
      auditRes.refetch()
      void refreshMe()
    } catch (error) {
      handleMutationError(error, push, 'Could not save mapping')
    }
  }, [capabilities.canEditSettings, push, usersRes, auditRes, refreshMe])

  const revokeAllSessions = React.useCallback(async () => {
    if (!capabilities.canEditSettings) {
      push({ variant: 'warn', title: '403 — Owner only.' })
      return
    }
    try {
      await apiPost('/auth/revoke-all')
      push({ variant: 'info', title: 'All sessions revoked', sub: 'You have been signed out.' })
      setViewer(null)
      setSession({ status: 'anonymous', userId: null, role: null })
    } catch (error) {
      handleMutationError(error, push, 'Could not revoke sessions')
    }
  }, [capabilities.canEditSettings, push])

  const createUser = React.useCallback(async (body: {
    username: string
    displayName: string
    mcUsername?: string
    role: 'owner' | 'admin' | 'player'
    accentColor: string
    home?: string
    password: string
  }) => {
    await apiPost<User>('/users', body)
    usersRes.refetch()
  }, [usersRes])

  const updateUser = React.useCallback(async (id: string, body: Partial<{
    displayName: string
    mcUsername: string
    role: 'owner' | 'admin' | 'player'
    accentColor: string
    home: string
    password: string
  }>) => {
    await apiPatch<User>(`/users/${id}`, body)
    usersRes.refetch()
  }, [usersRes])

  const deleteUser = React.useCallback(async (id: string) => {
    await apiDelete(`/users/${id}`)
    usersRes.refetch()
  }, [usersRes])

  const serverPanelName = React.useCallback((serverId: string) => {
    const server = servers.find(s => s.id === serverId)
    return server?.name ?? '(panel)'
  }, [servers])

  return {
    session,
    viewer,
    capabilities,
    devMode,
    servers,
    serversError: serversRes.error,
    serversLoading: serversRes.loading,
    refetchServers: serversRes.refetch,
    vps,
    vpsError: vpsRes.error,
    vpsLoading: vpsRes.loading,
    refetchVps: vpsRes.refetch,
    logs,
    audit,
    auditError: auditRes.error,
    auditLoading: auditRes.loading,
    refetchAudit: auditRes.refetch,
    players,
    playersError: playersRes.error,
    playersLoading: playersRes.loading,
    refetchPlayers: playersRes.refetch,
    pendingPlugins: plugins.pending,
    installedPlugins: plugins.installed,
    pluginsError: pluginsRes.error,
    pluginsLoading: pluginsRes.loading,
    refetchPlugins: pluginsRes.refetch,
    users,
    usersError: usersRes.error,
    usersLoading: usersRes.loading,
    refetchUsers: usersRes.refetch,
    settings,
    meLoading,
    login,
    logout,
    clearConsole,
    sendConsoleCommand,
    runLifecycleAction,
    runQuickAction,
    approvePlugin,
    rejectPlugin,
    toggleInstalledPlugin,
    deleteInstalledPlugin,
    uploadPlugin,
    fetchPeople,
    mutatePeople,
    fetchProperties,
    writeProperties,
    fetchLivePlayers,
    fetchOfflinePlayers,
    fetchItemRegistry,
    editPlayerInventory,
    fetchShulkerContents,
    editShulkerContents,
    backups: backupsRes.data ?? { archives: [], freeDiskGb: 0 },
    backupsError: backupsRes.error,
    backupsLoading: backupsRes.loading,
    refetchBackups: backupsRes.refetch,
    triggerBackup,
    restoreBackup,
    saveMapping,
    revokeAllSessions,
    createUser,
    updateUser,
    deleteUser,
    serverPanelName,
  }
}

function capitalize(s: string): string {
  return s ? s[0].toUpperCase() + s.slice(1) : s
}

function newKey(): string {
  return `op_${Math.random().toString(36).slice(2, 10)}_${Date.now().toString(36)}`
}

function handleMutationError(error: unknown, push: ToastPush, defaultTitle: string) {
  if (isApiError(error)) {
    if (error.status === 409) {
      const busy = extractServerBusy(error.detail)
      if (busy) {
        push({
          variant: 'warn',
          title: 'Server busy',
          sub: `${busy.label} · by ${busy.requestedBy}`,
          mono: true,
        })
        return
      }
    }
    push({ variant: error.status === 403 ? 'warn' : 'error', title: defaultTitle, sub: error.message })
  } else {
    push({ variant: 'error', title: defaultTitle, sub: 'Network error' })
  }
}

function extractServerBusy(detail: unknown): { label: string; kind: string; requestedBy: string; activeOperationId: string } | null {
  if (!detail || typeof detail !== 'object') return null
  const inner = (detail as { detail?: unknown }).detail
  if (!inner || typeof inner !== 'object') return null
  const record = inner as Record<string, unknown>
  if (record.error !== 'server_busy') return null
  return {
    activeOperationId: String(record.activeOperationId ?? ''),
    label: String(record.label ?? record.kind ?? 'operation'),
    kind: String(record.kind ?? ''),
    requestedBy: String(record.requestedBy ?? 'unknown'),
  }
}

function appendConsoleEcho(
  echoes: Record<ServerId, LocalConsoleEcho[]>,
  serverId: ServerId,
  line: LogLine,
): Record<ServerId, LocalConsoleEcho[]> {
  const next = [
    ...trimConsoleEchoes(echoes[serverId] ?? []),
    { atMs: Date.now(), line },
  ].slice(-LOCAL_CONSOLE_ECHO_LIMIT)
  return { ...echoes, [serverId]: next }
}

function mergeConsoleLogs(serverLogs: LogLine[], echoes: LocalConsoleEcho[]): LogLine[] {
  const activeEchoes = trimConsoleEchoes(echoes)
  const merged = [
    ...serverLogs.map((line, index) => ({
      line,
      atMs: parseLogClock(line.t),
      index,
      local: false,
    })),
    ...activeEchoes.map((entry, index) => ({
      line: entry.line,
      atMs: timeOfDayMs(entry.atMs),
      index,
      local: true,
    })),
  ]

  merged.sort((left, right) => {
    if (left.atMs !== right.atMs) return left.atMs - right.atMs
    if (left.local !== right.local) return left.local ? 1 : -1
    return left.index - right.index
  })

  return merged.slice(-200).map(entry => entry.line)
}

function trimConsoleEchoes(echoes: LocalConsoleEcho[], now: number = Date.now()): LocalConsoleEcho[] {
  return echoes.slice(-LOCAL_CONSOLE_ECHO_LIMIT)
}

function parseLogClock(clock: string): number {
  const match = /^(\d{2}):(\d{2}):(\d{2})$/.exec(clock.trim())
  if (!match) return Number.MAX_SAFE_INTEGER
  const [, hh, mm, ss] = match
  return ((Number(hh) * 60 + Number(mm)) * 60 + Number(ss)) * 1000
}

function timeOfDayMs(timestamp: number): number {
  const current = new Date(timestamp)
  return ((current.getHours() * 60 + current.getMinutes()) * 60 + current.getSeconds()) * 1000
}

function describeQuickAction(
  request: QuickActionRequest,
  response: QuickActionResponse,
  viewer: User | null,
): { title: string; sub: string; mono: boolean; consoleLine: LogLine | null } {
  const payload = response.audit.payload ?? {}
  const displayName = typeof payload.displayName === 'string' ? payload.displayName : viewer?.displayName
  const text = typeof payload.text === 'string' ? payload.text : null
  const isBroadcast = payload.transport === 'broadcast'

  if (displayName && text && (request.template === 'say' || isBroadcast)) {
    return {
      title: 'Message sent',
      sub: `${displayName}: ${text}`,
      mono: false,
      consoleLine: null,
    }
  }

  const command = normalizeCommandText(request, response)
  return {
    title: 'Command sent',
    sub: command,
    mono: true,
    consoleLine: null,
  }
}

function normalizeCommandText(request: QuickActionRequest, response: QuickActionResponse): string {
  const raw = request.template === 'raw_rcon' ? request.payload?.cmd : null
  if (typeof raw === 'string' && raw.trim()) {
    return raw.trim().replace(/^\/+/, '')
  }
  return response.command
}

function currentConsoleClock(): string {
  const now = new Date()
  return [now.getHours(), now.getMinutes(), now.getSeconds()]
    .map(part => String(part).padStart(2, '0'))
    .join(':')
}
